import { useState } from 'react'
import { useRapidApiUsage } from '@/hooks/useRapidApiUsage'
import { useSourceHealth } from '@/hooks/useSourceHealth'
import type { AnalyticsPeriod } from '@/hooks/useAnalytics'
import type { RapidApiProviderUsage, SourceHealthItem } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { AlertTriangle, RefreshCw, Gauge } from 'lucide-react'
import { formatNumber, formatRelativeTime, cn } from '@/lib/utils'

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  tiktok: 'TikTok',
  google_news: 'Google News',
}

const HEALTH_LABELS: Record<SourceHealthItem['status'], string> = {
  healthy: 'Sana',
  suspect: 'Sospechosa',
  broken: 'Rota',
  no_data: 'Sin datos',
}

const HEALTH_BADGE_CLASSES: Record<SourceHealthItem['status'], string> = {
  healthy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  suspect: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  broken: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300',
  no_data: 'bg-muted text-muted-foreground',
}

const TREND_COLORS: Record<string, string> = {
  facebook: '#5B8FA8',
  instagram: '#C4644A',
  twitter: '#7BA68A',
  tiktok: '#8B7EC8',
}

const FALLBACK_COLOR = '#D4942A'

function barColor(percent: number | null): string {
  if (percent === null) return 'bg-muted-foreground/40'
  if (percent >= 90) return 'bg-red-500'
  if (percent >= 70) return 'bg-amber-500'
  return 'bg-primary'
}

/** resets_at está en el futuro: "en X días/horas". */
function resetLabel(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(diff) || diff <= 0) return 'pronto'
  const days = Math.floor(diff / 86_400_000)
  if (days >= 1) return `en ${days} día${days > 1 ? 's' : ''}`
  const hours = Math.floor(diff / 3_600_000)
  if (hours >= 1) return `en ${hours} h`
  const mins = Math.floor(diff / 60_000)
  return `en ${mins} min`
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('es', { month: 'short', day: 'numeric' })
}

function ProviderCard({ provider }: { provider: RapidApiProviderUsage }) {
  const label = PLATFORM_LABELS[provider.platform] ?? provider.platform
  const pct = provider.percent_used
  const nearLimit = pct !== null && pct >= 80

  return (
    <Card className={cn(nearLimit && 'border-amber-400/60')}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base capitalize">{label}</CardTitle>
          {nearLimit && (
            <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Cerca del límite" />
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">{provider.host}</p>
      </CardHeader>
      <CardContent>
        {provider.no_data ? (
          <p className="py-4 text-sm text-muted-foreground">
            Sin datos aún — se llenará tras la próxima búsqueda en esta plataforma.
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold">
                {formatNumber(provider.requests_used ?? 0)}
              </span>
              <span className="text-sm text-muted-foreground">
                / {provider.requests_limit !== null ? formatNumber(provider.requests_limit) : '—'}{' '}
                usadas
              </span>
            </div>

            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn('h-full rounded-full transition-all', barColor(pct))}
                style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
                role="progressbar"
                aria-valuenow={pct ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${label}: ${pct ?? 0}% de la cuota usada`}
              />
            </div>

            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {provider.requests_remaining !== null
                  ? `${formatNumber(provider.requests_remaining)} restantes`
                  : 'restantes: —'}
              </span>
              <span
                className={cn(
                  'font-medium',
                  nearLimit ? 'text-amber-600' : 'text-muted-foreground'
                )}
              >
                {pct !== null ? `${pct}%` : '—'}
              </span>
            </div>

            <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
              {provider.resets_at && <p>Renueva {resetLabel(provider.resets_at)}</p>}
              {provider.observed_at && (
                <p>Actualizado {formatRelativeTime(provider.observed_at)}</p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SourceHealthSection() {
  const { data, isLoading, isError } = useSourceHealth()
  const sources = data?.sources ?? []
  const broken = sources.filter((s) => s.status === 'broken')

  return (
    <>
      {broken.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Fuente de scraping rota</p>
            <p>
              {broken.map((s) => PLATFORM_LABELS[s.source] ?? s.source).join(', ')}{' '}
              {broken.length > 1 ? 'llevan' : 'lleva'} varias búsquedas seguidas fallando. Revisa el
              proveedor en RapidAPI (¿cambió el formato de respuesta o murió el vendor?).
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Salud de fuentes</CardTitle>
          <p className="text-xs text-muted-foreground">
            Rachas de búsquedas consecutivas sin datos por fuente. Una fuente que falla o devuelve
            vacío muchas veces seguidas suele ser un scraper roto, no keywords sin resultados.
          </p>
        </CardHeader>
        <CardContent>
          {isError ? (
            <p className="py-4 text-sm text-muted-foreground">
              No se pudo cargar la salud de fuentes.
            </p>
          ) : isLoading ? (
            <Skeleton className="h-40" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Fuente</th>
                    <th className="pb-2 pr-4 font-medium">Estado</th>
                    <th className="pb-2 pr-4 font-medium">Última búsqueda</th>
                    <th className="pb-2 pr-4 font-medium">Racha sin datos</th>
                    <th className="pb-2 font-medium">Último error</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => (
                    <tr key={s.source} className="border-b last:border-0">
                      <td className="py-2.5 pr-4 font-medium">
                        {PLATFORM_LABELS[s.source] ?? s.source}
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge className={cn('border-transparent', HEALTH_BADGE_CLASSES[s.status])}>
                          {HEALTH_LABELS[s.status]}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-4 text-muted-foreground">
                        {s.last_report_at ? (
                          <>
                            {formatRelativeTime(s.last_report_at)}
                            {s.last_posts_count !== null && ` · ${s.last_posts_count} posts`}
                            {s.last_status && ` · ${s.last_status}`}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2.5 pr-4">
                        {s.consecutive_zero > 0 ? (
                          <span
                            className={cn(
                              s.status === 'broken' || s.status === 'suspect'
                                ? 'font-semibold text-red-600 dark:text-red-400'
                                : 'text-muted-foreground'
                            )}
                          >
                            {s.consecutive_zero} vacías
                            {s.consecutive_failed > 0 && ` (${s.consecutive_failed} fallidas)`}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </td>
                      <td
                        className="max-w-[280px] truncate py-2.5 text-xs text-muted-foreground"
                        title={s.last_error ?? undefined}
                      >
                        {s.last_error ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}

export const RapidApiUsagePage = () => {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d')
  const { data, isLoading, isError, refetch, isFetching } = useRapidApiUsage(period)

  const providers = data?.providers ?? []
  const critical = providers.filter((p) => p.percent_used !== null && p.percent_used >= 90)
  const trend = data?.daily_trend ?? []
  const trendKeys = Array.from(
    new Set(trend.flatMap((row) => Object.keys(row).filter((k) => k !== 'date')))
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <Gauge className="h-7 w-7 text-muted-foreground" />
            Uso de RapidAPI
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Consumo de cuota de las membresías RapidAPI (origen de posts y comentarios). Datos
            reales reportados por el scraper.
          </p>
        </div>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}>
          <TabsList>
            <TabsTrigger value="7d">7d</TabsTrigger>
            <TabsTrigger value="30d">30d</TabsTrigger>
            <TabsTrigger value="90d">90d</TabsTrigger>
            <TabsTrigger value="6m">6m</TabsTrigger>
            <TabsTrigger value="1y">1a</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Critical banner */}
      {critical.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Cuota casi agotada</p>
            <p>
              {critical.map((p) => PLATFORM_LABELS[p.platform] ?? p.platform).join(', ')} por encima
              del 90% de la cuota. Considera ampliar el plan de RapidAPI para no interrumpir el
              scraping.
            </p>
          </div>
        </div>
      )}

      {/* Error state */}
      {isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <p className="text-sm text-muted-foreground">No se pudo cargar el uso de RapidAPI.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-44" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {providers.map((p) => (
            <ProviderCard key={p.host} provider={p} />
          ))}
        </div>
      )}

      {/* Salud de fuentes (Fase A) */}
      <SourceHealthSection />

      {/* Trend chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Consumo acumulado por día</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64" />
          ) : trend.length && trendKeys.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={formatDateLabel} />
                <Legend />
                {trendKeys.map((key) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={PLATFORM_LABELS[key] ?? key}
                    stroke={TREND_COLORS[key] ?? FALLBACK_COLOR}
                    fill={TREND_COLORS[key] ?? FALLBACK_COLOR}
                    fillOpacity={0.12}
                    strokeWidth={2}
                    connectNulls
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              Sin datos de consumo aún. Se irá poblando a medida que el scraper procese búsquedas.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
